import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Car, Users, Ticket, ShoppingBag, Play, ChevronRight } from 'lucide-react';

// Unsplash placeholder images (same as original site)
const HERO_IMAGE = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80';
const QUICK_LINK_IMAGES = [
  'https://images.unsplash.com/photo-1504707748692-419802cf939d?w=400&q=80',
  'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400&q=80',
  'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=400&q=80',
];

const quickLinks = [
  {
    title: 'Book Track Time',
    description: 'Reserve exclusive access to our world-class facilities',
    href: '/book',
    image: QUICK_LINK_IMAGES[0],
  },
  {
    title: 'Upcoming Events',
    description: 'View races, track days, and special events',
    href: '/calendar',
    image: QUICK_LINK_IMAGES[1],
  },
  {
    title: 'STEM Programs',
    description: 'Inspiring the next generation of motorsport talent',
    href: '/about',
    image: QUICK_LINK_IMAGES[2],
  },
];

const newsTickerItems = [
  'Welcome to DriveIT NQ - Book your track time today!',
  'New time slots now available for Main Circuit bookings',
  'Join our STEM education programs - Inspiring the next generation',
  'Check out our upcoming events in the calendar',
];

export function HomePage() {
  return (
    <div className="min-h-screen">
      {/* News Ticker */}
      <div className="bg-red-600 text-white py-2 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap">
          {newsTickerItems.map((item, i) => (
            <span key={i} className="mx-8 inline-block">
              {item}
              <span className="mx-4 text-red-300">•</span>
            </span>
          ))}
          {newsTickerItems.map((item, i) => (
            <span key={`dup-${i}`} className="mx-8 inline-block">
              {item}
              <span className="mx-4 text-red-300">•</span>
            </span>
          ))}
        </div>
      </div>

      {/* Hero Section with Video */}
      <section className="relative h-[70vh] min-h-[500px] max-h-[800px] overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_IMAGE}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/race1v.mp4" type="video/mp4" />
        </video>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Content */}
        <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold text-white mb-6 drop-shadow-lg">
              DriveIT NQ
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed">
              North Queensland's Premier Motorsport Facility. Experience world-class
              racing on our FIA-certified circuit, drag strip, and training facilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/book"
                className="btn btn-lg bg-red-600 hover:bg-red-700 text-white shadow-lg"
              >
                Book Track Time
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link
                to="/track-rentals"
                className="btn btn-lg bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-gray-900"
              >
                <Play size={20} className="mr-2" />
                View Our Tracks
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            {quickLinks.map((link, index) => (
              <Link
                key={index}
                to={link.href}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-[4/3] relative">
                  <img
                    src={link.image}
                    alt={link.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-red-400 transition-colors">
                      {link.title}
                    </h3>
                    <p className="text-gray-300 text-sm">{link.description}</p>
                    <div className="mt-3 flex items-center text-red-400 text-sm font-medium">
                      Learn more
                      <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Facilities</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              World-class motorsport infrastructure designed for professionals and enthusiasts alike
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Circuit */}
            <div className="card p-6 card-hover border-t-4 border-red-600">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <Car className="text-red-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Main Circuit</h3>
              <p className="text-gray-600 mb-4">
                2.75km FIA Grade 3 circuit with 12 challenging corners. Perfect for
                racing teams and serious track day enthusiasts.
              </p>
              <ul className="text-sm text-gray-500 space-y-1 mb-4">
                <li>• FIA Grade 3 certified</li>
                <li>• 12 technical corners</li>
                <li>• Professional pit facilities</li>
              </ul>
              <div className="text-2xl font-bold text-red-600">$4,500/slot</div>
            </div>

            {/* Drag Strip */}
            <div className="card p-6 card-hover border-t-4 border-blue-600">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <ArrowRight className="text-blue-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Drag Strip</h3>
              <p className="text-gray-600 mb-4">
                Quarter-mile IHRA-spec drag strip with professional timing tower
                and spectator facilities.
              </p>
              <ul className="text-sm text-gray-500 space-y-1 mb-4">
                <li>• IHRA specification</li>
                <li>• Professional timing system</li>
                <li>• Burnout box</li>
              </ul>
              <div className="text-2xl font-bold text-blue-600">$3,200/slot</div>
            </div>

            {/* Skid Pan */}
            <div className="card p-6 card-hover border-t-4 border-green-600">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="text-green-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Skid Pan</h3>
              <p className="text-gray-600 mb-4">
                Purpose-built driver training and drift area with wet and dry
                surface options.
              </p>
              <ul className="text-sm text-gray-500 space-y-1 mb-4">
                <li>• Wet surface capability</li>
                <li>• Drift-ready surface</li>
                <li>• Training equipment</li>
              </ul>
              <div className="text-2xl font-bold text-green-600">$1,800/slot</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-500 mb-2">2.75km</div>
              <div className="text-gray-400">Main Circuit Length</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-500 mb-2">12</div>
              <div className="text-gray-400">Technical Corners</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-500 mb-2">100+</div>
              <div className="text-gray-400">Teams Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-500 mb-2">500+</div>
              <div className="text-gray-400">Events Per Year</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-3xl p-8 md:p-12 text-center text-white shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to hit the track?</h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Register your team today and start booking track time at North
              Queensland's premier motorsport facility.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="btn btn-lg bg-white text-red-600 hover:bg-gray-100 shadow-lg"
              >
                <Users size={20} className="mr-2" />
                Create Account
              </Link>
              <Link
                to="/calendar"
                className="btn btn-lg border-2 border-white text-white hover:bg-white hover:text-red-600"
              >
                <Calendar size={20} className="mr-2" />
                View Calendar
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Links */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/tickets"
              className="flex items-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow group"
            >
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                <Ticket className="text-purple-600" size={28} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                  Event Tickets
                </h3>
                <p className="text-gray-600 text-sm">Purchase tickets for upcoming races and events</p>
              </div>
              <ChevronRight className="text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
            </Link>

            <Link
              to="/shop"
              className="flex items-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow group"
            >
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
                <ShoppingBag className="text-orange-600" size={28} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                  Merchandise Shop
                </h3>
                <p className="text-gray-600 text-sm">Official DriveIT NQ apparel and accessories</p>
              </div>
              <ChevronRight className="text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
